const allowedTags = new Set([
  "a",
  "b",
  "blockquote",
  "br",
  "div",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "i",
  "li",
  "ol",
  "p",
  "span",
  "strong",
  "u",
  "ul",
]);

const allowedClassPattern = /^(ql-(align|size|indent)-[a-z0-9-]+|ql-align-(center|right|justify))$/i;
const allowedStyleProps = new Set([
  "background-color",
  "color",
  "font-size",
  "font-style",
  "font-weight",
  "text-align",
  "text-decoration",
]);

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const sanitizeUrl = (value = "") => {
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  if (/^(https?:|mailto:|tel:)/i.test(trimmed) || trimmed.startsWith("/")) return trimmed;
  return "";
};

const sanitizeStyle = (value = "") =>
  String(value)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const [rawProp, ...rawValueParts] = rule.split(":");
      const prop = String(rawProp || "").trim().toLowerCase();
      const styleValue = rawValueParts.join(":").trim();
      if (!allowedStyleProps.has(prop)) return "";
      if (!styleValue || /expression|javascript:|url\s*\(/i.test(styleValue)) return "";
      if (!/^[#(),.%\-\w\s]+$/.test(styleValue)) return "";
      return `${prop}: ${styleValue}`;
    })
    .filter(Boolean)
    .join("; ");

const unwrapElement = (element) => {
  const parent = element.parentNode;
  if (!parent) return;
  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
};

const cleanElement = (element) => {
  Array.from(element.children).forEach(cleanElement);

  const tag = element.tagName?.toLowerCase();
  if (!allowedTags.has(tag)) {
    unwrapElement(element);
    return;
  }

  Array.from(element.attributes).forEach((attribute) => {
    const name = attribute.name.toLowerCase();
    const value = attribute.value;

    if (tag === "a" && name === "href") {
      const safeHref = sanitizeUrl(value);
      if (safeHref) {
        element.setAttribute("href", safeHref);
        element.setAttribute("target", "_blank");
        element.setAttribute("rel", "noopener noreferrer");
      } else {
        element.removeAttribute(name);
      }
      return;
    }

    if (name === "class") {
      const safeClasses = String(value)
        .split(/\s+/)
        .filter((className) => allowedClassPattern.test(className))
        .join(" ");
      if (safeClasses) element.setAttribute("class", safeClasses);
      else element.removeAttribute(name);
      return;
    }

    if (name === "style") {
      const safeStyle = sanitizeStyle(value);
      if (safeStyle) element.setAttribute("style", safeStyle);
      else element.removeAttribute(name);
      return;
    }

    element.removeAttribute(name);
  });
};

export const getPlainTextFromHtml = (value = "") => {
  if (typeof window === "undefined" || !window.DOMParser) {
    return String(value).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }

  const container = document.createElement("div");
  container.innerHTML = String(value || "");
  return container.textContent.replace(/\s+/g, " ").trim();
};

export const sanitizeRichHtml = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  if (typeof window === "undefined" || !window.DOMParser) {
    return escapeHtml(raw).replace(/\n/g, "<br />");
  }

  const hasHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
  const parser = new window.DOMParser();
  const html = hasHtml ? raw : escapeHtml(raw).replace(/\n/g, "<br />");
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  if (!root) return "";

  Array.from(root.children).forEach(cleanElement);
  return root.innerHTML;
};

export const buildRichHtmlMarkup = (value = "") => ({
  __html: sanitizeRichHtml(value),
});
